import React, { useState, useEffect, useCallback } from 'react';

// Global lightbox state
let openLightbox = null;
let setGalleryImages = null;

export const triggerLightbox = (src, allImages = null) => {
  if (allImages && setGalleryImages) {
    setGalleryImages(allImages);
  }
  if (openLightbox) openLightbox(src);
};

const ImageLightbox = () => {
  const [src, setSrc] = useState(null);
  const [scale, setScale] = useState(1);
  const [closing, setClosing] = useState(false);
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Register global opener
  useEffect(() => {
    openLightbox = (imgSrc) => {
      setSrc(imgSrc);
      setScale(1);
      setClosing(false);
      document.body.style.overflow = 'hidden';
    };
    setGalleryImages = (imgs) => {
      setImages(imgs);
    };
    return () => {
      openLightbox = null;
      setGalleryImages = null;
    };
  }, []);

  // Update current index when src changes
  useEffect(() => {
    if (src && images.length > 0) {
      const idx = images.findIndex(img => img === src || img.includes(src.split('/').pop()));
      if (idx !== -1) setCurrentIndex(idx);
    }
  }, [src, images]);

  const close = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setSrc(null);
      setClosing(false);
      setImages([]);
      document.body.style.overflow = '';
    }, 200);
  }, []);

  const goNext = useCallback((e) => {
    if (e) e.stopPropagation();
    if (images.length > 0) {
      const nextIdx = (currentIndex + 1) % images.length;
      setCurrentIndex(nextIdx);
      setSrc(images[nextIdx]);
      setScale(1);
    }
  }, [images, currentIndex]);

  const goPrev = useCallback((e) => {
    if (e) e.stopPropagation();
    if (images.length > 0) {
      const prevIdx = (currentIndex - 1 + images.length) % images.length;
      setCurrentIndex(prevIdx);
      setSrc(images[prevIdx]);
      setScale(1);
    }
  }, [images, currentIndex]);

  // Keyboard: Escape to close, +/- to zoom, arrows for prev/next
  useEffect(() => {
    if (!src) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === '+' || e.key === '=') setScale(s => Math.min(s + 0.25, 3));
      if (e.key === '-') setScale(s => Math.max(s - 0.25, 0.5));
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [src, close, goNext, goPrev]);

  // Click interceptor for images inside project pages
  useEffect(() => {
    const handler = (e) => {
      const img = e.target.closest('img');
      if (!img) return;
      // Only trigger for images inside project detail sections
      const inProject = img.closest('[data-project-content]') || img.closest('section');
      if (!inProject) return;
      // Skip tiny images (icons, logos under 80px)
      if (img.naturalWidth && img.naturalWidth < 80) return;
      if (img.width < 80) return;

      e.preventDefault();

      // Collect all images in the same container
      const container = img.closest('[data-project-content]') || img.closest('section');
      const allImgs = Array.from(container.querySelectorAll('img'))
        .filter(i => i.width >= 80)
        .map(i => i.src);

      if (allImgs.length > 0) {
        setImages(allImgs);
      }

      if (openLightbox) openLightbox(img.src);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  if (!src) return null;

  const hasMultiple = images.length > 1;
  const navButtonStyle = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    border: '1px solid rgba(255,255,255,0.2)',
    background: 'rgba(0,0,0,0.5)',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    zIndex: 10001,
    backdropFilter: 'blur(8px)',
    transition: 'all 0.2s ease'
  };

  return (
    <div
      onClick={close}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: closing ? 'rgba(0,0,0,0)' : 'rgba(0,0,0,0.95)',
        backdropFilter: 'blur(20px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'zoom-out',
        transition: 'background 0.2s ease',
        opacity: closing ? 0 : 1
      }}
    >
      {/* Close button */}
      <button
        onClick={close}
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          border: '1px solid rgba(255,255,255,0.15)',
          background: 'rgba(255,255,255,0.08)',
          color: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          zIndex: 10001,
          backdropFilter: 'blur(8px)'
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>

      {/* Previous button */}
      {hasMultiple && (
        <button
          onClick={goPrev}
          style={{ ...navButtonStyle, left: '20px' }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>
      )}

      {/* Next button */}
      {hasMultiple && (
        <button
          onClick={goNext}
          style={{ ...navButtonStyle, right: '20px' }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>
      )}

      {/* Image counter */}
      {hasMultiple && (
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 16px',
          borderRadius: '20px',
          background: 'rgba(0,0,0,0.6)',
          color: '#fff',
          fontSize: '14px',
          fontWeight: '500',
          zIndex: 10001
        }}>
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {/* Zoom controls */}
      <div style={{
        position: 'absolute',
        bottom: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '8px',
        zIndex: 10001
      }}>
        <button
          onClick={(e) => { e.stopPropagation(); setScale(s => Math.max(s - 0.25, 0.5)); }}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.08)',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            backdropFilter: 'blur(8px)'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14"/></svg>
        </button>
        <span
          onClick={(e) => { e.stopPropagation(); setScale(1); }}
          style={{
            padding: '6px 14px',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.08)',
            color: '#fff',
            fontSize: '13px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            backdropFilter: 'blur(8px)'
          }}
        >
          {Math.round(scale * 100)}%
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); setScale(s => Math.min(s + 0.25, 3)); }}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.08)',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            backdropFilter: 'blur(8px)'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
        </button>
      </div>

      {/* Image */}
      <img
        src={src}
        alt=""
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '85vw',
          maxHeight: '80vh',
          objectFit: 'contain',
          borderRadius: '8px',
          transform: `scale(${scale})`,
          transition: 'transform 0.2s ease',
          cursor: scale > 1 ? 'grab' : 'default',
          userSelect: 'none'
        }}
      />
    </div>
  );
};

export default ImageLightbox;
